<template>
  <div class="registration">
    <div class="registration__info">
      <LogoDigmedia />
      <ul class="registration__info-text-box">
        <li>App Version: {{ appVersion }}</li>
        <li>IP: {{ deviceIp }}</li>
        <li>Env: {{ getEnvironment }}</li>
        <li>{{ getTimeString }}</li>
        <li>Device: {{ $store.state.deviceId }}</li>
        <li>Building: {{ $store.state.buildingId }} ({{ registratedBuildingInfo.buildingId }})</li>
        <li>Group: {{ $store.state.groupId }} ({{ registratedBuildingInfo.groupId }})</li>
      </ul>
    </div>
    <div class="registration-settings">
      <template v-if="fingerprintChecking">
        <div class="registration-settings__info">
          <p class="registration-settings__info-text">Gerät wird erkannt...</p>
        </div>
      </template>
      <template v-else-if="!hasBuildingId">
        <RegistrationInput
          title="Hash ID"
          :value="getHashLastDigits"
          :subline="getHashId"
        />
        <div class="registration-settings__info">
          <h2 class="registration-settings__info-title">Info</h2>
          <p class="registration-settings__info-text">
            Go to the Admin tool and assign device to building by hash ID (url: {{ adminUrl }})
          </p>
          <p class="registration-settings__info-text">
            Afterwards press ENTER/SUBMIT
          </p>
        </div>
      </template>
      <template v-else>
        <!-- <Input
          title="Device ID"
          :value="getDeviceIdLastDigits"
          :subline="getDeviceId"
        /> -->
        <div class="registration-settings__info">
          <h2 class="registration-settings__info-title">Building</h2>
          <p class="registration-settings__info-text">
            {{ getBuildingId }} – {{ getBuildingName }}
          </p>
        </div>
        <RegistrationInput
          title="Room number / Location:"
          v-model="room"
          placeholder="Room No."
          :disabled="false"
          :active="selectedIndex === 0"
          @select="() => handleSelectInput(0)"
        />
        <div class="registration-submit">
          <RegistrationButton
            :isActive="selectedIndex === 1"
            @click="handleSubmit"
          >
            Create Device
          </RegistrationButton>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import router from '@/router';
import { keyHandler, KEYS } from '@/mixins/keyHandler';
import Registration from '@/helpers/Registration';
import LogoDigmedia from '@/assets/logos/LogoDigmedia.vue';
import RegistrationInput from '@/components/RegistrationInput.vue';
import RegistrationButton from '@/components/RegistrationButton.vue';
import { NAV_TYPE } from '@/store/modules/navigation';
import Device from '@/helpers/Device';

export default {
  mixins: [keyHandler],
  components: { LogoDigmedia, RegistrationInput, RegistrationButton },
  props: {},
  emits: {},
  data() {
    return {
      time: new Date(),
      timeInterval: null,
      registratedBuildingInfo: {},
      room: '',
      selectedIndex: 0,
      selectElementCounter: 1,
      keyStore: [],
      keyStoreTimer: 3000,
      keyStoreInterval: undefined,
      deviceIp: '',
      fingerprintChecking: true,
    };
  },
  async mounted() {
    // Kein removeData() hier: Router landet uns nur dann auf dieser Seite,
    // wenn keine vollstaendigen Daten gefunden wurden. Ein pauschales Loeschen
    // wuerde Spiegelkopien (Cookie/sessionStorage), die der Fingerprint- bzw.
    // Hash-Pfad noch ueberschreibt, voreilig zerstoeren.
    Promise.resolve(Device.ip).then(ip => { this.deviceIp = ip; });

    // Fingerprint-Lookup: bekanntes Geraet automatisch erkennen und direkt zum Home
    try {
      const existingDevice = await Registration.tryAutoRegisterByFingerprint();
      if (existingDevice) {
        this.$store.dispatch('notification/new', {
          title: 'Auto-Registriert',
          message: `DeviceId: ${existingDevice.id}`,
        });
        await this.$store.dispatch('device/saveDeviceData', {
          buildingId: existingDevice.buildingId,
          deviceId: existingDevice.id,
          room: existingDevice.room,
        });
        this.$store.dispatch('customizer/fetchCustomizerSettings');
        this.$store.dispatch('navigation/init');
        setTimeout(() => {
          this.$store.dispatch('navigation/to', NAV_TYPE.HOME);
        }, 500);
        return;
      }
    } catch (err) {
      console.log('[Registration] Fingerprint auto-check error:', err);
    }

    // Kein Fingerprint bekannt → normaler Registrierungsvorgang
    this.fingerprintChecking = false;
    this.requestBuildingInfo();
    this.timeInterval = setInterval(() => {
      this.time = new Date();
    }, 500);
  },
  destroyed() {
    clearInterval(this.timeInterval);
  },
  computed: {
    appVersion() {
      return process.env.APP_VERSION || '';
    },
    adminUrl() {
      return process.env.RUN_ENV === 'production'
        ? 'https://admin.digmedia.net'
        : 'https://stagingadmin.digmedia.net';
    },
    hasBid() {
      return !!Registration.getData.bid;
    },
    getEnvironment() {
      return process.env.RUN_ENV === 'production' ? 'Production' : 'Staging';
    },
    getTimeString() {
      let options = {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      };

      return this.time.toLocaleString('default', options);
    },
    getHashId() {
      return this.registratedBuildingInfo.id || '';
    },
    getHashLastDigits() {
      return this.getHashId.substring(this.getHashId.length - 4);
    },
    getDeviceId() {
      return this.registratedBuildingInfo.deviceId || '';
    },
    getDeviceIdLastDigits() {
      return this.getDeviceId.substring(this.getDeviceId.length - 4);
    },
    getBuildingId() {
      return this.registratedBuildingInfo.buildingId;
    },
    getBuildingName() {
      if (!this.registratedBuildingInfo.buildingDetails) return '';
      return this.registratedBuildingInfo.buildingDetails.name;
    },
    hasBuildingId() {
      return !!this.getBuildingId;
    },
  },
  watch: {
    keyStore(value) {
      console.log('WATCH keyStore,', value);

      if (value.join('') === '232323') {
        this.$store.dispatch('device/reboot');
      }
      if (value.length > 0) {
        if (this.keyStoreInterval) {
          this.keyStoreTimer = 3000;
        } else {
          this.keyStoreHandler();
        }
      }
      this.$store.dispatch('notification/new', {
        title: 'KeyStore',
        message: value.join('') === '232323',
      });
    },
  },
  methods: {
    keyStoreHandler() {
      this.keyStoreInterval = setInterval(() => {
        if (this.keyStoreTimer <= 0) {
          this.keyStore = [];
          clearInterval(this.keyStoreInterval);
          return;
        }
        this.keyStoreTimer -= 1000;
      }, 1000);
    },
    async requestBuildingInfo() {
      try {
        const response = await Registration.fetchBuildingInfo();
        this.registratedBuildingInfo = response;
      } catch (err) {
        console.log('err');
      }
    },
    eventHandler(key) {
      let value = parseInt(this[this.selectedElement]);
      if (!value) value = 0;
      switch (key) {
        case KEYS.KEY_2:
        case KEYS.KEY_3:
          this.$store.dispatch('notification/new', { title: key });
          this.keyStore.push(key);
          break;
        case KEYS.LEFT:
          window.location.reload(true);
          break;
        case KEYS.RIGHT:
          this.$store.dispatch('notification/new', {
            title: 'SUBMIT SETUP',
            message: `Room: ${this.room}, Index: ${this.selectedIndex}`,
          });
          if (this.selectedIndex === 1 && this.room) {
            this.$store.dispatch('notification/new', {
              title: 'SUBMIT SETUP',
            });
            this.handleSubmit();
            break;
          }
          break;
        case KEYS.ENTER:
          if (this.selectedIndex === 1 && this.room) {
            this.$store.dispatch('notification/new', {
              title: 'SUBMIT SETUP',
            });
            console.log('ROOM: ', this.room);
            this.handleSubmit();
            break;
          }
          if (!this.hasBuildingId) {
            this.$store.dispatch('notification/new', {
              title: 'Get Building Info',
            });
            this.requestBuildingInfo();
            break;
          }
          if (this.room.length > 0) {
            this.$store.dispatch('notification/new', {
              title: 'Get Building Info',
            });
            console.log('ROOM: ', this.room);
            this.handleSubmit();
          }
          break;
        case KEYS.DOWN:
          if (this.selectedIndex < this.selectElementCounter)
            this.selectedIndex++;
          break;
        case KEYS.UP:
          if (this.selectedIndex > 0) this.selectedIndex--;
          break;
      }
    },
    handleSelectInput(index) {
      this.selectedIndex = index;
    },
    async handleSubmit() {
      try {
        const response = await Registration.createDevice({
          ...this.registratedBuildingInfo,
          room: this.room,
        });
        console.log('DEVICE CREATED ', response);
        await this.$store.dispatch('device/saveDeviceData', response);
        this.$store.dispatch('customizer/fetchCustomizerSettings');
        this.$store.dispatch('navigation/init');

        this.$store.dispatch('notification/new', {
          title: 'Created',
          message: JSON.stringify(response),
        });
        setTimeout(() => {
          this.$store.dispatch('navigation/to', NAV_TYPE.HOME);
        }, 1000);
      } catch (err) {
        this.$store.dispatch('notification/new', {
          title: 'Error while create device',
          message: JSON.stringify(err),
        });
        console.log('Could not create device ', err);
      }
    },
  },
};
</script>

<style scoped>
.registration {
  background: #213541;
  display: flex;
  height: 100vh;
  padding: 60px 90px;
  width: 100vw;
}
.registration__info {
  text-align: center;
  flex: 0 0 30%;
  margin-right: 3rem;
}

.registration-settings {
  flex: 1 1 70%;
}
.registration-settings__info {
  margin-bottom: 3rem;
}

.registration__info-text-box li {
  font-size: 23px;
  margin-bottom: 0.3rem;
  line-height: 1.2;
}
.registration__info-text-box {
  background-color: #1d2d37;
  color: rgb(250, 250, 250);
  margin: 1rem 0;
  padding: 1rem 0.5rem;
}

.registration-settings__info-title {
  color: #6c8290;
  font-size: 32px;
  margin-bottom: 0.5rem;
}
.registration-settings__info-text {
  color: rgb(250, 250, 250);
  font-size: 32px;
  margin-bottom: 0.5rem;
}

.registration-submit {
  width: 100%;
  text-align: right;
}
</style>



// WEBPACK FOOTER //
// src/pages/Registration.vue